import { PieChart, Coffee, Lightbulb } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Dashboards";

export const tiles: ArticleTile[] = [
  { slug: "treasurer-dashboard", title: "Treasurer Dashboard Walkthrough", category: CATEGORY, description: "Tour every chart, stat card and report", icon: PieChart },
  { slug: "catering-dashboard", title: "Catering Dashboard & Stock Management", category: CATEGORY, description: "Track stock, batches and expiry dates", icon: Coffee },
];

export const articles: Record<string, ArticleContent> = {
  "treasurer-dashboard": {
    title: "Treasurer Dashboard Walkthrough",
    category: CATEGORY,
    readTime: "5 min read",
    content: (
      <div className="space-y-6">
        <Hero>The Treasurer dashboard is the financial heart of your PTA. Here's a tour of what each section shows and how to use it.</Hero>
        <Section icon={PieChart} title="Stat cards (top row)">
          <Bullets items={[
            "Total Income — all money received this financial year.",
            "Total Expenses — all money spent this financial year.",
            "Available Funds — what's spendable after ring-fencing and reserves.",
            "Bank Balance — running total carried over from last year.",
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Pie charts">
          <Bullets items={[
            "Income by source — see which fundraisers generate the most.",
            "Expenses by category — spot where money is going.",
            "Both use a pastel palette and £ values for clarity.",
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Reports tab">
          <Bullets items={[
            "Generate financial reports for any date range.",
            "Export to PDF for trustees, the AGM, or the bank.",
            "Filter by event to report on a single fundraiser.",
          ]} />
        </Section>
      </div>
    ),
  },
  "catering-dashboard": {
    title: "Catering Dashboard & Stock Management",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Run school cafés, breakfast clubs or event catering with batch-tracked stock and automatic expiry alerts.</Hero>
        <Section icon={Coffee} title="Add stock">
          <Steps items={[
            <>Open the <strong>Catering Dashboard</strong>.</>,
            <>Click <strong>Add Stock</strong> and select the item.</>,
            <>Enter quantity, batch number and expiry date.</>,
            <>Save — stock levels update instantly.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Returns and expiry">
          <Bullets items={[
            "Returned stock inherits the expiry of the soonest-expiring batch.",
            "The dashboard flags items expiring within 7 days.",
            "Use the Stock Log tab to audit every movement.",
          ]} />
        </Section>
        <Tip>The Catering dashboard requires the Catering add-on on your subscription.</Tip>
      </div>
    ),
  },
};
